<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import Button from 'dashboard/components-next/button/Button.vue';
import { HEADER_ACTION_BUTTON_CLASS } from 'dashboard/helper/conversationHeaderStyles';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const props = defineProps({
  separateActions: {
    type: Boolean,
    default: false,
  },
});
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);
const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store
    .dispatch('toggleStatus', payload)
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    })
    .catch(error => {
      // The Teletel server-side validation is authoritative — surface its
      // message (e.g. missing required fields) instead of failing silently.
      const serverMessage =
        error?.response?.data?.message || error?.response?.data?.error;
      useAlert(serverMessage || t('CONVERSATION.CHANGE_STATUS_FAILED'));
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing, unknown } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (unknown) {
    // Required-field state could not be determined client-side. Never assume
    // "no required fields": warn, then let the authoritative server-side
    // Teletel validation accept or reject the resolution.
    useAlert(t('CONVERSATION.REQUIRED_ATTRIBUTES_UNVERIFIED'));
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
    return;
  }

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center resolve-actions">
    <div
      class="flex-shrink-0"
      :class="
        props.separateActions
          ? 'flex items-center gap-2'
          : [
              'rounded-lg shadow outline-1 outline',
              !showOpenButton ? 'outline-n-container' : 'outline-transparent',
            ]
      "
    >
      <!-- Icon-only header actions (owner directive): labels moved to hover
           tooltips (kept) and aria-labels; no visible text. Coloured by the
           conversation STATE (owner): open blue, resolved red, pending or
           snoozed amber; the snooze action amber, mark-pending slate. Faded,
           so the icon itself carries the colour on a light ground. -->
      <Button
        v-if="isOpen"
        v-tooltip="t('CONVERSATION.HEADER.RESOLVE_ACTION_HELP')"
        :aria-label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="blue"
        faded
        no-animation
        :class="
          props.separateActions
            ? HEADER_ACTION_BUTTON_CLASS
            : 'ltr:rounded-r-none rtl:rounded-l-none !outline-0'
        "
        icon="i-lucide-check"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        v-tooltip="t('CONVERSATION.HEADER.REOPEN_ACTION_HELP')"
        :aria-label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="ruby"
        faded
        no-animation
        :class="
          props.separateActions
            ? HEADER_ACTION_BUTTON_CLASS
            : 'ltr:rounded-r-none rtl:rounded-l-none !outline-0'
        "
        icon="i-lucide-refresh-ccw"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        v-tooltip="t('CONVERSATION.HEADER.OPEN_ACTION_HELP')"
        :aria-label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="amber"
        faded
        no-animation
        :class="
          props.separateActions ? HEADER_ACTION_BUTTON_CLASS : '!outline-0'
        "
        icon="i-lucide-play"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        v-tooltip="t('CONVERSATION.HEADER.SNOOZE_ACTION_HELP')"
        :aria-label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
        size="sm"
        color="amber"
        faded
        no-animation
        :class="
          props.separateActions ? HEADER_ACTION_BUTTON_CLASS : '!outline-0'
        "
        icon="i-lucide-alarm-clock-minus"
        @click="openSnoozeModal"
      />
      <Button
        v-if="showAdditionalActions"
        v-tooltip="t('CONVERSATION.HEADER.PENDING_ACTION_HELP')"
        :aria-label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
        size="sm"
        color="slate"
        faded
        no-animation
        :class="
          props.separateActions ? HEADER_ACTION_BUTTON_CLASS : '!outline-0'
        "
        icon="i-lucide-circle-dot-dashed"
        @click="toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
      />
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
